/**
 * La jornada semanal: una cita fija con reglas propias.
 *
 * La razon no es el adorno, es la cola. Con base pequena, diez personas
 * repartidas por la semana no llenan ninguna sala; las mismas diez a la misma
 * hora son dos partidos. La cita **concentra a la gente en el tiempo**, y de
 * paso da un motivo concreto para volver.
 *
 * Todo se deriva de la clave de semana, asi que cliente y servidor calculan lo
 * mismo sin hablarlo. Aun asi, en online manda el servidor: es el que estampa
 * la regla en la sala, para que un reloj mal puesto no parta la partida en dos.
 */
import { MATCH } from "./config.js";
import { MATCH_MODES } from "./mode.js";
import type { OnlineModeId } from "./online.js";

/** Dia de la cita (0 = domingo) y franja horaria local. */
export const WEEKLY_EVENT = {
  DAY: 0,
  START_HOUR: 19,
  DURATION_HOURS: 3,
} as const;

export interface WeeklyRule {
  id: string;
  label: string;
  blurb: string;
  /** Modo de la jornada. Que todos jueguen al mismo llena las salas. */
  modeId: OnlineModeId;
  /** Multiplicador sobre la duracion normal del partido. */
  durationScale: number;
  /**
   * Multiplicador sobre el alto de porteria del modo.
   *
   * NUNCA por encima de 1: con la boca mas grande que `FIELD.GOAL_HEIGHT` se
   * desborda la geometria de punteria y el remate de primera deja de apuntar.
   */
  goalScale: number;
}

/**
 * Cinco jornadas que rotan. Todas se apoyan en palancas ya calibradas: duracion
 * y alto de porteria. Deliberadamente NO se tocan la friccion del balon ni la
 * velocidad del jugador: esos valores alimentan normalizadores en media docena
 * de sitios, y cambiarlos a ciegas es la clase de fallo silencioso que ya costo
 * caro con el recuento de jugadores de campo.
 */
export const WEEKLY_RULES: readonly WeeklyRule[] = [
  {
    id: "clasica",
    label: "Jornada clásica",
    blurb: "5v5, sin florituras. El fútbol de siempre.",
    modeId: "5v5",
    durationScale: 1,
    goalScale: 1,
  },
  {
    id: "cerrojo",
    label: "Cerrojo",
    blurb: "Porterías pequeñas en 5v5. Cada gol se suda.",
    modeId: "5v5",
    durationScale: 1,
    goalScale: 0.72,
  },
  {
    id: "relampago",
    label: "Relámpago",
    blurb: "3v3 a partidos cortos. Sin tiempo para pensar.",
    modeId: "3v3",
    durationScale: 0.6,
    goalScale: 1,
  },
  {
    id: "maraton",
    label: "Maratón",
    blurb: "7v7 largo. Gana quien aguante el bloque.",
    modeId: "7v7",
    durationScale: 1.6,
    goalScale: 1,
  },
  {
    id: "duelo",
    label: "Duelo",
    blurb: "2v2 corto y a cara de perro.",
    modeId: "2v2",
    durationScale: 0.7,
    goalScale: 0.85,
  },
];

/**
 * Clave de semana ISO, p. ej. "2026-W34".
 *
 * Vive aqui porque la usan la jornada y la clasificacion semanal de la carrera,
 * y dos implementaciones de lo mismo acaban discrepando en fin de ano.
 */
export function weekKeyOf(date: Date): string {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  // ISO: la semana pertenece al jueves que contiene.
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((d.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7);
  return `${d.getUTCFullYear()}-W${String(week).padStart(2, "0")}`;
}

/** Hash estable de la clave: la misma semana da siempre la misma jornada. */
function hashKey(key: string): number {
  let h = 2166136261;
  for (let i = 0; i < key.length; i++) {
    h ^= key.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

export function ruleForWeek(weekKey: string): WeeklyRule {
  return WEEKLY_RULES[hashKey(weekKey) % WEEKLY_RULES.length]!;
}

export function ruleById(id: string | undefined): WeeklyRule | null {
  if (!id) return null;
  return WEEKLY_RULES.find((r) => r.id === id) ?? null;
}

export interface WeeklyStatus {
  weekKey: string;
  rule: WeeklyRule;
  /** La cita esta abierta ahora mismo. */
  live: boolean;
  /** ms hasta que abre (0 si ya esta abierta). */
  startsInMs: number;
  /** ms hasta que cierra (0 si no esta abierta). */
  endsInMs: number;
}

/**
 * Ultima vez que ABRIO la cita, mirando hacia atras desde `now`.
 *
 * Se calcula hacia atras y no hacia delante a proposito: asi solo hay un caso
 * ("la de antes"), en vez de tener que distinguir si la de esta semana ya paso,
 * esta en curso o esta por venir.
 */
function lastOpening(now: Date): Date {
  const d = new Date(now);
  d.setHours(WEEKLY_EVENT.START_HOUR, 0, 0, 0);
  const back = (d.getDay() - WEEKLY_EVENT.DAY + 7) % 7;
  d.setDate(d.getDate() - back);
  // Hoy es el dia pero aun no es la hora: la ultima apertura fue la anterior.
  if (d.getTime() > now.getTime()) d.setDate(d.getDate() - 7);
  return d;
}

/**
 * Estado de la cita. La hora es LOCAL a proposito: para una comunidad de un
 * mismo huso, "los domingos a las siete" se recuerda; una hora UTC, no.
 */
export function weeklyStatus(now: Date = new Date()): WeeklyStatus {
  const durationMs = WEEKLY_EVENT.DURATION_HOURS * 3600_000;
  const opened = lastOpening(now);
  const closes = opened.getTime() + durationMs;
  const live = now.getTime() < closes;

  // Si esta abierta, la jornada es la suya; si no, la que viene.
  const relevant = live ? opened : new Date(opened.getTime() + 7 * 86_400_000);
  const weekKey = weekKeyOf(relevant);

  return {
    weekKey,
    rule: ruleForWeek(weekKey),
    live,
    startsInMs: live ? 0 : relevant.getTime() - now.getTime(),
    endsInMs: live ? closes - now.getTime() : 0,
  };
}

// ------------------------------------------------------------- aplicar reglas

/**
 * Aplica la jornada y devuelve la funcion que lo deshace.
 *
 * Muta config compartida, asi que hay dos cuidados que no son opcionales:
 * guardar los valores originales ANTES de tocar nada, y no permitir aplicar dos
 * veces seguidas — la segunda guardaria como "original" un valor ya modificado
 * y la partida siguiente arrancaria con la jornada pegada.
 */
let activeRevert: (() => void) | null = null;

export function applyWeeklyRule(rule: WeeklyRule): () => void {
  // Aplicar encima de otra jornada: se deshace la anterior primero.
  activeRevert?.();

  const mode = MATCH_MODES[rule.modeId];
  const previousDuration = MATCH.DURATION;
  const previousGoal = mode.physics.goalScale;

  (MATCH as { DURATION: number }).DURATION = Math.round(previousDuration * rule.durationScale);
  (mode.physics as { goalScale: number }).goalScale = previousGoal * rule.goalScale;

  const revert = (): void => {
    if (activeRevert !== revert) return;
    (MATCH as { DURATION: number }).DURATION = previousDuration;
    (mode.physics as { goalScale: number }).goalScale = previousGoal;
    activeRevert = null;
  };
  activeRevert = revert;
  return revert;
}

/** Deshace la jornada activa, si la hay. Seguro de llamar siempre. */
export function clearWeeklyRule(): void {
  activeRevert?.();
}

/** Solo para pruebas y diagnostico: si hay una jornada puesta ahora mismo. */
export function weeklyRuleActive(): boolean {
  return activeRevert !== null;
}
